import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronDown } from "lucide-react";
import { useMakes } from "@/hooks/useMakes";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";
import { commonButtonLabels } from "@/i18n";

interface MakeSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMake: string;
  onSelect: (make: string) => void;
  vehicleType?: "car" | "mc";
}

export const MakeSelectionDialog = ({
  open,
  onOpenChange,
  selectedMake,
  onSelect,
  vehicleType = "car",
}: MakeSelectionDialogProps) => {
  const { language } = useLanguage();
  const { isNative } = useNativePlatform();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};
  const btn = commonButtonLabels(language);
  const [showAllMakes, setShowAllMakes] = useState(false);
  const [showMakeOtherInput, setShowMakeOtherInput] = useState(false);
  const [makeOtherText, setMakeOtherText] = useState("");

  const { makes, isLoadingMakes } = useMakes(true);

  // Filter makes based on vehicle type
  const filteredMakes = useMemo(() => {
    if (!makes.length) return [];
    return makes.filter((m) => (vehicleType === "mc" ? m.isMC : !m.isMC));
  }, [makes, vehicleType]);

  // Get popular makes first
  const popularMakes = useMemo(() => {
    return filteredMakes.filter((m) => m.isPopular);
  }, [filteredMakes]);

  // Get non-popular makes
  const otherMakes = useMemo(() => {
    return filteredMakes.filter((m) => !m.isPopular);
  }, [filteredMakes]);

  // Display makes: show all popular makes initially, then others if showAllMakes is true
  // Always keep "Övrigt" as the last item
  // For motorcycles (mc), show all makes directly (no "Mer" button needed)
  const displayedMakes = useMemo(() => {
    // For motorcycles, always show all makes directly
    if (vehicleType === "mc") {
      return filteredMakes;
    }

    if (showAllMakes) {
      // When showing all, still put Övrigt last
      return [...popularMakes, ...otherMakes];
    }
    // Show all popular makes initially
    return popularMakes;
  }, [popularMakes, otherMakes, showAllMakes, vehicleType, filteredMakes]);

  const handleMakeSelect = (makeName: string) => {
    onSelect(makeName);
    onOpenChange(false);
    setShowMakeOtherInput(false);
    setMakeOtherText("");
  };

  const handleMakeOtherClick = () => {
    setShowMakeOtherInput(true);
    // If we already have a make value, pre-fill it
    if (selectedMake && !makeOtherText) {
      setMakeOtherText(selectedMake);
    }
  };

  const handleMakeOtherSubmit = () => {
    if (makeOtherText.trim()) {
      handleMakeSelect(makeOtherText.trim());
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        nested={isNative}
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{L.selectMake ?? "Välj märke"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isLoadingMakes ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              {L.loadingMakes ?? "Laddar märken..."}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {displayedMakes.map((makeItem) => (
                  <Button
                    key={makeItem.id}
                    variant="formOption"
                    onClick={() => handleMakeSelect(makeItem.name)}
                    className={`${
                      selectedMake === makeItem.name
                        ? "border-accent border-2"
                        : "border-gray-400"
                    } truncate`}
                    title={makeItem.name}
                  >
                    {makeItem.name}
                  </Button>
                ))}
                {/* Övrigt option */}
                {!showMakeOtherInput && (
                  <Button
                    variant="formOption"
                    onClick={handleMakeOtherClick}
                    className="bg-blue-200 dark:bg-blue-900/30 border-blue-400"
                  >
                    {L.other ?? "Övrigt"}
                  </Button>
                )}
              </div>

              {showMakeOtherInput && (
                <div className="flex gap-2">
                  <Input
                    value={makeOtherText}
                    onChange={(e) => setMakeOtherText(e.target.value)}
                    placeholder={L.enterMake ?? "Ange märke (max 30 tecken)"}
                    maxLength={30}
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleMakeOtherSubmit();
                      }
                    }}
                  />
                  <Button
                    onClick={handleMakeOtherSubmit}
                    disabled={!makeOtherText.trim()}
                  >
                    {btn?.save ?? "Spara"}
                  </Button>
                </div>
              )}

              {/* Only show "Mer" button for cars, not for motorcycles */}
              {vehicleType !== "mc" && otherMakes.length > 0 && (
                <Button
                  variant="ghost"
                  className="w-full flex justify-center gap-2"
                  onClick={() => setShowAllMakes(!showAllMakes)}
                >
                  {showAllMakes
                    ? (L.showLess ?? "Visa färre")
                    : (L.more ?? "Mer")}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      showAllMakes ? "rotate-180" : ""
                    }`}
                  />
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
